// PRD decision #10: all UI chrome and error copy are CLIENT-SIDE. We map the
// server's `error.code` to user-facing strings here; the server `message` is
// kept only for debugging. (flavorText / quest outcomes come from the server.)
import type { CoreErrorCode } from "./types";

export class ApiError extends Error {
  constructor(
    public readonly code: CoreErrorCode,
    /** Raw server message — for logs, not for display. */
    public readonly serverMessage: string,
    public readonly status: number
  ) {
    super(serverMessage);
    this.name = "ApiError";
  }

  /** User-facing copy for this error code. */
  get display(): string {
    return ERROR_COPY[this.code] ?? ERROR_COPY_FALLBACK;
  }
}

const ERROR_COPY_FALLBACK = "Something went wrong. Try again.";

const ERROR_COPY: Record<CoreErrorCode, string> = {
  invalid_request: "Please open Rocky from inside the chat.",
  unauthorized: "Your session expired. Reopen the app.",
  missing_chat_context: "Please open Rocky from inside the chat.",
  not_group_chat: "Rocky lives in group chats — open from the group.",
  setup_incomplete: "Finish setup to start playing.",
  pet_dead: "Rocky has passed out. Revive him first.",
  no_active_quest: "No quest is running right now.",
  rate_limited: "Too fast — give Rocky a moment.",
  not_found: "We couldn't find that.",
  internal_error: "Rocky tripped over something. Try again.",
  geo_unresolved: "Couldn't place you on the map from this connection. Try again later.",
};

/** Error codes that mean "chat context is missing/invalid" → show ChatGate. */
export function isChatContextError(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    (e.code === "missing_chat_context" || e.code === "invalid_request" || e.code === "not_group_chat")
  );
}

export function isSetupIncomplete(e: unknown): boolean {
  return e instanceof ApiError && e.code === "setup_incomplete";
}

export function isPetDead(e: unknown): boolean {
  return e instanceof ApiError && e.code === "pet_dead";
}

export function isUnauthorized(e: unknown): boolean {
  return e instanceof ApiError && e.code === "unauthorized";
}
